import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';

function ChecklistPage({ user }) {
  const { id } = useParams(); // 'opening' or 'closing'
  const navigate = useNavigate();
  const [checklist, setChecklist] = useState(null);
  const [completedItems, setCompletedItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchChecklist();
  }, [id]);

  const fetchChecklist = async () => {
    try {
      const response = await axios.get(`${API}/checklists/${id}`);
      setChecklist(response.data);
    } catch (error) {
      toast.error('Failed to fetch checklist');
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (itemId) => {
    setCompletedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSubmit = async () => {
    if (completedItems.length === 0) {
      toast.error('Please complete at least one item');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/checklist-submissions`, {
        type: id,
        completed_items: completedItems,
        notes
      });
      toast.success('Checklist submitted successfully!');
      navigate('/checklists');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit checklist');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!checklist) return null;

  // Calculate total items across all sections
  const totalItems = checklist.sections?.reduce((sum, section) => sum + section.items.length, 0) || 0;
  const completionPercentage = totalItems > 0 ? (completedItems.length / totalItems) * 100 : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-primary/5 pb-24">
      <header className="glass border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/checklists')}
            data-testid="back-button"
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-lg text-gray-900 capitalize">{id} Checklist</h1>
            <p className="text-xs text-gray-500">{completedItems.length} of {totalItems} completed</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Progress Bar */}
        <div className="bg-white rounded-2xl p-6 shadow-md mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Progress</span>
            <span className="text-sm font-bold text-primary">{completionPercentage.toFixed(0)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-primary h-3 rounded-full transition-all duration-300"
              style={{ width: `${completionPercentage}%` }}
            ></div>
          </div>
        </div>

        {/* Checklist Items Grouped by Sections */}
        <div className="space-y-6 mb-6">
          {checklist.sections?.map((section) => (
            <div key={section.id} className="space-y-3">
              {/* Section Header */}
              <div className="bg-primary/10 rounded-xl p-3">
                <h3 className="font-bold text-primary text-sm">{section.title}</h3>
              </div>
              
              {/* Section Items */}
              {section.items.map((item) => {
                const isCompleted = completedItems.includes(item.id);
                return (
                  <div
                    key={item.id}
                    onClick={() => toggleItem(item.id)}
                    data-testid={`checklist-item-${item.id}`}
                    className={`bg-white rounded-xl p-4 shadow-md cursor-pointer transition-all ${
                      isCompleted ? 'border-2 border-green-500 bg-green-50' : 'border-2 border-gray-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        isCompleted ? 'bg-green-500 border-green-500' : 'border-gray-300'
                      }`}>
                        {isCompleted && <CheckCircle2 className="h-4 w-4 text-white" />}
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm ${
                          isCompleted ? 'text-green-900 font-medium' : 'text-gray-700'
                        }`}>
                          {item.text}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Notes */}
        <div className="bg-white rounded-2xl p-6 shadow-md mb-6">
          <Label htmlFor="notes" className="text-sm font-medium text-gray-700 mb-2">Notes (Optional)</Label>
          <textarea
            id="notes"
            data-testid="checklist-notes-input"
            placeholder="Any observations or issues..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full h-24 mt-2 px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </main>

      {/* Submit Button */}
      <div className="fixed bottom-6 left-0 right-0 px-4 z-50">
        <div className="max-w-4xl mx-auto">
          <Button
            onClick={handleSubmit}
            data-testid="submit-checklist-button"
            className="w-full h-14 bg-primary hover:bg-primary-light text-white font-bold rounded-2xl shadow-xl transition-colors"
            disabled={submitting || completedItems.length === 0}
          >
            {submitting ? 'Submitting...' : 'Submit Checklist'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ChecklistPage;